import { createForm, useCreateFormStore } from '@components/form';
import { IconButton } from '@components/icon-button';
import { IconCloseRegular } from '@components/icons/close-regular';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useCreateBlacklistedEmail, useUpdateBlacklistedEmail } from 'api/blacklisted/hooks';
import { BlacklistedEmail } from 'api/blacklisted/requests';
import { Modal, ModalProps } from 'features/modals';

type Props = ModalProps<{
  blockedEmail?: BlacklistedEmail;
}>;

const Form = createForm<BlacklistedEmail>();

export const AddBlackListEmail = (props: Props) => {
  const { closeModal, blockedEmail } = props;
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const createMutation = useCreateBlacklistedEmail();
  const updateMutation = useUpdateBlacklistedEmail();

  const isEdit = Boolean(blockedEmail);

  const handleSave = (data: BlacklistedEmail) => {
    if (isEdit) {
      updateMutation.mutate({ ...blockedEmail, ...data });
    } else {
      createMutation.mutate(data);
    }
    closeModal();
  };

  return (
    <Modal>
      <Form store={formStore} onSubmit={handleSave}>
        <Stack space={4}>
          <Stack orientation="horizontal" space={2} justify="space-between">
            <Text
              font="heading1"
              color="purple100"
              text={isEdit ? t('editTitle') : t('modalTitle')}
            />
            <IconButton variant="ghost-purple" onClick={closeModal} icon={<IconCloseRegular />} />
          </Stack>
          <Stack space={4}>
            <Form.TextField
              id="email"
              label={t('email')}
              validators={['required', 'email']}
              defaultValue={blockedEmail?.email}
            />
            <Form.TextField
              id="description"
              label={t('reason')}
              defaultValue={blockedEmail?.description}
            />
          </Stack>
        </Stack>
        <Form.Actions>
          <Form.Reset text={t('cancel')} onClick={closeModal} />
          <Form.Submit text={isEdit ? t('save') : t('modalTitle')} variant="primary-red" />
        </Form.Actions>
      </Form>
    </Modal>
  );
};

const locale = {
  en: {
    modalTitle: 'Block email',
    editTitle: 'Edit details',
    email: 'Email',
    reason: 'Reason for blocking',
    cancel: 'Cancel',
    save: 'Save',
  },
  is: {
    modalTitle: 'Loka á netfang',
    editTitle: 'Breyta upplýsingum',
    edit: 'Breyta',
    unblock: 'opna fyrir netfang',
    email: 'Netfang',
    reason: 'Ástæða fyrir lokun',
    cancel: 'Hætta við',
    save: 'Vista',
  },
};
